import { useTranslation } from 'react-i18next';
import { Card, CardContent } from 'components/ui/card';
import { TokenUsageMetrics } from 'features/usage/services/usage-service';
import { formatNumber, formatPercent, getAverage } from 'features/usage/utils/usage-format';
import { UsageCardHeader } from './usage-card-header';

function TokenShareRow({
  label,
  value,
  total,
  hint,
  avg,
  barClassName,
}: {
  label: string;
  value: number;
  total: number;
  hint: string;
  avg: string;
  barClassName: string;
}) {
  const { t } = useTranslation();
  const share = total ? (value / total) * 100 : 0;

  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
        <span className="font-medium text-foreground">{label}</span>
        <span>
          <span className="text-foreground">{formatNumber(value)}</span>{' '}
          <span className="text-muted-foreground">({formatPercent(share)})</span>
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        {value > 0 && (
          <div
            className={`h-full min-w-2 rounded-full ${barClassName}`}
            style={{ width: `${Math.min(100, share)}%` }}
          />
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {hint} · {t('AVG_PER_REQ', { avg })}
      </p>
    </div>
  );
}

export function UsageTokenUsageCard({
  totals,
  dateRange,
}: {
  totals: TokenUsageMetrics;
  dateRange: string;
}) {
  const { t } = useTranslation();
  const total = totals.total_tokens;
  const turns = totals.turn_count;

  return (
    <Card className="min-w-0 rounded-section border-border/70 shadow-none">
      <CardContent className="flex flex-col gap-5 p-5 md:p-6">
        <UsageCardHeader title={t('TOKEN_USAGE', { defaultValue: 'Token usage' })} subtitle={dateRange} />

        <div className="flex flex-wrap items-center justify-between gap-x-3 border-b border-border pb-3">
          <span className="text-sm text-muted-foreground">{t('TOTAL_TOKENS')}</span>
          <span
            title={formatNumber(total)}
            className="truncate text-2xl font-medium leading-heading text-foreground sm:text-3xl sm:leading-heading"
          >
            {formatNumber(total)}
          </span>
        </div>

        <div className="space-y-4">
          <TokenShareRow
            label={t('INPUT_TOKENS')}
            value={totals.standard_input_tokens}
            total={total}
            hint={t('USAGE_INPUT_HINT', { defaultValue: 'New text sent to the AI' })}
            avg={getAverage(totals.standard_input_tokens, turns)}
            barClassName="bg-usage-chart-1"
          />
          <TokenShareRow
            label={t('CACHED_INPUT_TOKENS', { defaultValue: 'Cached Input Tokens' })}
            value={totals.cached_input_tokens}
            total={total}
            hint={t('USAGE_CACHED_INPUT_HINT', {
              defaultValue: 'Earlier context reused, instead of sent again',
            })}
            avg={getAverage(totals.cached_input_tokens, turns)}
            barClassName="bg-usage-chart-2"
          />
          <TokenShareRow
            label={t('CACHE_WRITE_TOKENS', { defaultValue: 'Cache Write Tokens' })}
            value={totals.cache_creation_input_tokens}
            total={total}
            hint={t('USAGE_CACHE_WRITE_HINT', { defaultValue: 'Context saved for reuse' })}
            avg={getAverage(totals.cache_creation_input_tokens, turns)}
            barClassName="bg-usage-chart-3"
          />
          <TokenShareRow
            label={t('OUTPUT_TOKENS')}
            value={totals.output_tokens}
            total={total}
            hint={t('USAGE_OUTPUT_HINT', { defaultValue: 'What the AI wrote back' })}
            avg={getAverage(totals.output_tokens, turns)}
            barClassName="bg-usage-chart-4"
          />
        </div>

        <p className="border-t border-border pt-4 text-xs text-muted-foreground">
          {t('IMAGE_INPUT_TOKENS', { defaultValue: 'Image Input Tokens' })}{' '}
          {formatNumber(totals.input_image_tokens)} ·{' '}
          {t('IMAGE_OUTPUT_TOKENS', { defaultValue: 'Image Output Tokens' })}{' '}
          {formatNumber(totals.output_image_tokens)}
        </p>
      </CardContent>
    </Card>
  );
}
